import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import '../table.css';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import DeleteDialog from './DeleteDialog';
import RestoreUserDialog from './RestoreUser';
import axios from 'axios';
import { BACKEND_URL_PORTAIL } from '../../../config/const';

const BasicTable = ({ searchTerm, initialData, fetchUserProfile, source, onSelectionChange, selectedUserIds, currentSolution }) => {
    const navigate = useNavigate();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isDialogRestoreOpen, setIsDialogRestoreOpen] = useState(false);
    const [unblockMessage, setUnblockMessage] = useState(null);
    const [userId, setUserId] = useState(null);
    const [hoveredUserId, setHoveredUserId] = useState(null);
    const [userName, setUserName] = useState("");
    const [selectedIds, setSelectedIds] = useState([]);

    // Welcome email state
    const [openWelcomeDialog, setOpenWelcomeDialog] = useState(false);
    const [selectedUserForEmail, setSelectedUserForEmail] = useState(null);
    const [sendEmail, setSendEmail] = useState(null);

    const { t } = useTranslation();

    const paginationOptions = {
        rowsPerPageText: t('rowsPerPage'),
        rangeSeparatorText: t('of'),
        selectAllRowsItem: true,
        selectAllRowsItemText: t('all'),
    };

    useEffect(() => {
        setSelectedIds(selectedUserIds || []);
    }, [selectedUserIds]);

    const handleMouseEnter = (id) => setHoveredUserId(id);
    const handleMouseLeave = () => setHoveredUserId(null);

    function getUser(idUser) {
        const config = {
            headers: {
                Authorization: 'Bearer ' + localStorage.getItem('token'),
            },
        };
        axios.get(BACKEND_URL_PORTAIL + 'api/getUserById/' + idUser + '/' + localStorage.getItem("currentOrg"), config).then((response) => {
            setUserName(response.firstname + " " + response.lastname);
        });
    }

    const handleUnblockUser = (userId) => {
        const config = {
            headers: {
                Authorization: 'Bearer ' + localStorage.getItem('token')
            }
        };
        const data = { idUser: userId };
        axios.post(BACKEND_URL_PORTAIL + 'api/unblockUser', data, config)
            .then(response => {
                setUnblockMessage(true);
                setTimeout(() => {
                    setUnblockMessage(null);
                    window.location.reload();
                }, 2000);
            })
            .catch(error => console.error(error));
    };

    const goToUpdateUser = (userId) => {
        localStorage.setItem('source', source);
        navigate(`/update-user/${userId}`);
    };

    const handleOpenDialog = (id) => {
        setUserName("");
        getUser(id);
        setUserId(id);
        setIsDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
        setUserId(null);
    };

    const handleOpenRestore = (id) => {
        setUserId(id);
        setIsDialogRestoreOpen(true);
    };

    const handleCloseRestore = () => {
        setIsDialogRestoreOpen(false);
        setUserId(null);
    };

    // Welcome email handlers
    const handleOpenWelcomeDialog = (userId) => {
        setSelectedUserForEmail(userId);
        setOpenWelcomeDialog(true);
    };

    const handleCloseWelcomeDialog = () => {
        setOpenWelcomeDialog(false);
        setSelectedUserForEmail(null);
    };

    const sendWelcomeEmail = () => {
        const config = {
            headers: {
                Authorization: 'Bearer ' + localStorage.getItem('token'),
                'Content-Type': 'application/json'
            }
        };
        const data = {
            idUser: selectedUserForEmail,
            language: localStorage.getItem('lang')
        };
        axios.post(BACKEND_URL_PORTAIL + 'api/sendWelcomeEmail', data, config)
            .then(() => {
                handleCloseWelcomeDialog();
                setSendEmail(true);
                setTimeout(() => {
                    setSendEmail(null);
                    window.location.reload();
                }, 2000);
            })
            .catch((error) => {
                console.error(error);
                handleCloseWelcomeDialog();
                setSendEmail(false);
                setTimeout(() => {
                    setSendEmail(null);
                    window.location.reload();
                }, 2000);
            });
    };

    const toggleRowSelection = (id) => {
        let newSelected;
        if (selectedIds.includes(id)) {
            newSelected = selectedIds.filter(item => item !== id);
        } else {
            newSelected = [...selectedIds, id];
        }
        setSelectedIds(newSelected);
        onSelectionChange(newSelected);
    };

    const columns = [
        {
            name: <span className='font-weight-bold fs-14'>{t('selected')}</span>,
            cell: row => (
                <input
                    type="checkbox"
                    checked={selectedIds.includes(row.idUser)}
                    onChange={() => toggleRowSelection(row.idUser)}
                />
            ),
            className: 'table-header',
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        },
        {
            name: <span className='font-weight-bold fs-14'>{t('username')}</span>,
            selector: row => (<span className='fontTable'>{row.username}</span>),
            sortable: true,
            className: 'table-header',
            sortFunction: (a, b) => a.username.localeCompare(b.username),
        },
        {
            name: <span className='font-weight-bold fs-14'>{t('firstName')}</span>,
            selector: row => (<span className='fontTable'>{row.firstname}</span>),
            sortable: true,
            className: 'table-header',
            sortFunction: (a, b) => a.firstname.localeCompare(b.firstname),
        },
        {
            name: <span className='font-weight-bold fs-15'>{t('lastName')}</span>,
            selector: row => (<span className='fontTable'>{row.lastname}</span>),
            sortable: true,
            className: 'table-header',
            sortFunction: (a, b) => a.lastname.localeCompare(b.lastname),
        },
        {
            name: <span className='font-weight-bold fs-15'>{t('status')}</span>,
            sortable: true,
            selector: (cell) => {
                switch (cell.status) {
                    case "disabled":
                        return <span className="badge" style={{ backgroundColor: 'orange' }}>{t(cell.status)}</span>;
                    case "created":
                        return <span className="badge bg-info">{t(cell.status)}</span>;
                    case "enabled":
                        return <span className="badge bg-success">{t(cell.status)}</span>;
                    default:
                        return <span className="badge bg-danger">{t(cell.status)}</span>;
                }
            },
            className: 'table-header',
            sortFunction: (a, b) => a.status.localeCompare(b.status),
        },
        {
            name: <span className='font-weight-bold fs-13'>{t('action')}</span>,
            sortable: true,
            cell: row => (
                <div className="d-flex gap-2">
                    <div className="edit">
                        <button
                            className="btn btn-outline-info waves-effect waves-light"
                            onClick={() => goToUpdateUser(row.idUser)}
                            title={t('update')}
                        >
                            <i className="ri-pencil-fill align-bottom"></i>
                        </button>
                    </div>

                    {row.status === "deleted" ? (
                        <div className="remove">
                            <button
                                className="btn btn-outline-success waves-effect waves-light"
                                onClick={() => handleOpenRestore(row.idUser)}
                                title={t('restore')}
                            >
                                <i className="fas fa-undo align-bottom"></i>
                            </button>
                        </div>
                    ) : (
                        <div className="remove">
                            <button
                                className="btn btn-outline-danger waves-effect waves-light"
                                onClick={() => handleOpenDialog(row.idUser)}
                                title={t('delete')}
                            >
                                <i className="ri-delete-bin-fill align-bottom"></i>
                            </button>
                        </div>
                    )}

                    {/* Send welcome email button — visible for "created" users on solution 2 */}
                    {row.status === "created" && JSON.parse(localStorage.getItem("currentSolution")).some(s => s.id == 2) && (
                          
                                <button
                                    className="btn btn-outline-primary waves-effect waves-light"
                                    onClick={() => handleOpenWelcomeDialog(row.idUser)}
                                    title={t('send')}
                                >
                                    <i className="ri-mail-line align-bottom"></i>
                                </button>
                            
                        )}

                    {row.failedLoginAttempts >= 5 && row.available && (
                        <button
                            className={`custom-unblock-btn ${hoveredUserId === row.idUser ? "btn btn-outline-danger" : "btn btn-danger"}`}
                            onClick={() => handleUnblockUser(row.idUser)}
                            title={t('UNBLOCK')}
                            onMouseEnter={() => handleMouseEnter(row.idUser)}
                            onMouseLeave={handleMouseLeave}
                        >
                            <i className="ri-lock-unlock-line align-bottom"></i>
                        </button>
                    )}

                    {isDialogOpen && (
                        <DeleteDialog
                            isOpen={isDialogOpen}
                            fetchUserProfile={fetchUserProfile}
                            handleClose={handleCloseDialog}
                            userId={userId}
                            userName={userName}
                        />
                    )}
                    {isDialogRestoreOpen && (
                        <RestoreUserDialog
                            isOpen={isDialogRestoreOpen}
                            fetchUserProfile={fetchUserProfile}
                            handleClose={handleCloseRestore}
                            userId={userId}
                        />
                    )}

                    {/* Welcome email confirmation dialog */}
                    {openWelcomeDialog && selectedUserForEmail === row.idUser && (
                        <div
                            style={{
                                position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                                backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1300,
                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}
                        >
                            <div
                                style={{
                                    backgroundColor: '#fff', borderRadius: '8px', padding: '24px',
                                    minWidth: '360px', maxWidth: '480px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
                                }}
                            >
                                <h5 style={{ marginBottom: '12px' }}>{t('sendWelcomeEmail')}</h5>
                                <p style={{ color: '#555', marginBottom: '24px' }}>{t('confirmSendWelcomeEmail')}</p>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                                    <button className="btn btn-outline-secondary" onClick={handleCloseWelcomeDialog}>
                                        {t('CANCEL')}
                                    </button>
                                    <button className="btn btn-primary" onClick={sendWelcomeEmail}>
                                        {t('send')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            ),
            className: 'table-header'
        }
    ];

    const [filteredData, setFilteredData] = useState([]);

    useEffect(() => {
        if (initialData && initialData.length > 0) {
            const filtered = initialData.filter(item =>
                item.idUser.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.lastname.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.firstname.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.status.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredData(filtered);
        } else {
            setFilteredData([]);
        }
    }, [searchTerm, initialData]);

    return (
        <>
            {/* Toast notifications */}
            {sendEmail === true && (
                <div className="alert alert-success" role="alert" style={{ marginBottom: '12px' }}>
                    {t('MailSuccess')}
                </div>
            )}
            {sendEmail === false && (
                <div className="alert alert-danger" role="alert" style={{ marginBottom: '12px' }}>
                    {t('MailFailed')}
                </div>
            )}

            <DataTable
                columns={columns}
                data={searchTerm ? filteredData : initialData}
                pagination
                paginationPerPage={50}
                paginationRowsPerPageOptions={[10, 25, 50, 100, 300]}
                paginationComponentOptions={paginationOptions}
                noDataComponent={t('noDataMessage')}
            />
        </>
    );
};

export { BasicTable };
